import { Link, useLoaderData, useParams, Await } from 'react-router-dom'
import { Suspense, useEffect, useState } from 'react'
import { Grid, Typography, Divider, Checkbox, FormControlLabel, FormGroup, Button, Box } from '@mui/material'
import { formatPrice } from '../../helpers/format'
import type { orderDetailInterface } from './VerificationType'
import type { verificationType } from '../../api/CustomerVerificationType'
import VerificationNewItem from './VerificationNewItem'
import VerificationChangeItem from './VerificationChangeItem'
import UpfrontPayment from './UpfrontPayment'
import VerificationPayment from './VerificationPayment'
import * as VerificationApi from '../../api/CustomerVerificationApi'
import { useForm, Controller } from "react-hook-form"
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';

const infoGridStyle = {
  '@media (min-width: 600px)': {
    mt: '5px',
  },
}

const infoGridHeaderStyle = {
  '@media (min-width: 0px)': {
    mt: '5px',
  },
  '@media (min-width: 600px)': {
    mt: '5px',
  },
}

const LinkStyle = {
  color: '#0185fe',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' }
}

const schema = Yup.object().shape({
  verifyChecked: Yup.boolean().required("Please agree to Alltel's Terms and Conditions to confirm your order.")
    .oneOf([true], "Please agree to Alltel's Terms and Conditions to confirm your order."),
  paymentTermsChecked: Yup.boolean().required("Please agree to Alltel's Payment Terms to confirm your order.")
    .oneOf([true], "Please agree to Alltel's Payment Terms to confirm your order."),
  upfront_required: Yup.boolean(),
  payment_required: Yup.boolean().required(),
  payment_type: Yup.string().typeError("payment type is required"),
  card_number: Yup.string().when('upfront_required', {
    is: true,
    then: (schema) => schema.required('credit card number is required')
  }),
  card_expiry_month: Yup.string().when('upfront_required', {
    is: true,
    then: (schema) => schema.required('required').matches(/^(0[1-9]|1[0-2])$/, 'Invalid')
  }),
  card_expiry_year: Yup.string().when('upfront_required', {
    is: true,
    then: (schema) => schema.required('required').matches(/^\d{2}$/, 'Invalid')
  }),
  card_holder_name: Yup.string().when('upfront_required', {
    is: true,
    then: (schema) => schema.required('card holder name is required')
  }),
  cvv: Yup.string().when('upfront_required', {
    is: true,
    then: (schema) => schema.required('CVV is required').matches(/^\d{3,4}$/, 'Invalid CVV')
  }),
  bank_account_name: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'bank_account',
    then: (schema) => schema.required('Bank account name is required')
  }),
  bank_account_bsb: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'bank_account',
    then: (schema) => schema.required('Bank account bsb is required')
  }),
  bank_account_number: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'bank_account',
    then: (schema) => schema.required('Bank account number is required').matches(/^\d+$/, 'Invalid number')
  }),
  monthly_card_holder_name: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'monthly_credit_card',
    then: (schema) => schema.required('card holder name is required')
  }),
  monthly_card_number: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'monthly_credit_card',
    then: (schema) => schema.required('credit card number is required')
  }),
  monthly_card_expiry_month: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'monthly_credit_card',
    then: (schema) => schema.required('required').matches(/^(0[1-9]|1[0-2])$/, 'Invalid')
  }),
  monthly_card_expiry_year: Yup.string().when(['payment_type', 'payment_required'], {
    is: (payment_type: string, payment_required: boolean) => payment_required === true && payment_type === 'monthly_credit_card',
    then: (schema) => schema.required('required').matches(/^\d{2}$/, 'Invalid')
  }),
});

type schemaType = Yup.InferType<typeof schema>;

type verificationParam = {
  uuid: string;
};

const VerificationPage = () => {
  const data = useLoaderData() as { orderDetails: Promise<orderDetailInterface> }

  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress size={48} sx={{ color: '#003187' }} />
      </Box>
    }>
      <Await resolve={data.orderDetails}>
        {(orderDetails: orderDetailInterface) => <VerificationPageContent orderDetails={orderDetails} />}
      </Await>
    </Suspense>
  )
}

const VerificationPageContent = ({ orderDetails }: { orderDetails: orderDetailInterface }) => {
  let { uuid } = useParams<verificationParam>();
  const [loading, setLoading] = useState(false)
  const [upfrontPaymentMade, setUpfrontPaymentMade] = useState(orderDetails.upfront_payment_made === 1)
  const [useDefaultMethod, setUseDefaultMethod] = useState(true)

  const { control, handleSubmit, formState: { errors }, setValue, setError } = useForm<schemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      verifyChecked: orderDetails.provisioning_checklist.customer_verified === 1 ? true : false,
      paymentTermsChecked: orderDetails.provisioning_checklist.customer_verified === 1 ? true : false,
      payment_type: 'credit_card',
      card_number: '',
      card_expiry_month: '',
      card_expiry_year: '',
      card_holder_name: '',
      cvv: '',
      bank_account_name: '',
      bank_account_bsb: '',
      bank_account_number: '',
      monthly_card_holder_name: '',
      monthly_card_number: '',
      monthly_card_expiry_month: '',
      monthly_card_expiry_year: '',
      upfront_required: false,
      payment_required: false,
    },
  });

  useEffect(() => {
    const needsUpfront = orderDetails.checklist_required.upfront_payment_required === 1 && !upfrontPaymentMade
    const needsMonthly = orderDetails.checklist_required.payment_verification_required === 1 && orderDetails.provisioning_checklist.payment_verified === 0

    setValue('upfront_required', needsUpfront)

    if (needsUpfront && needsMonthly && useDefaultMethod) {
      setValue('payment_required', false)
      setValue('payment_type', 'credit_card')
    } else if (needsMonthly) {
      setValue('payment_required', true)
      setValue('payment_type', 'monthly_credit_card')
    } else {
      setValue('payment_required', false)
      if (needsUpfront) {
        setValue('payment_type', 'credit_card')
      }
    }
  }, [setValue, orderDetails.provisioning_checklist, orderDetails.checklist_required, upfrontPaymentMade, useDefaultMethod])

  const verifyOrder = async (props: schemaType) => {
    const needsUpfront = props.upfront_required
    const isMonthlyCreditCard = props.payment_type === 'monthly_credit_card'
    const isBankAccount = props.payment_type === 'bank_account'

    const payload: verificationType = {
      order_uuid: uuid,
      ...(needsUpfront && {
        paymentMethod: 'creditCard',
        cardNumber: props.card_number,
        cardholderName: props.card_holder_name,
        expiryDateMonth: props.card_expiry_month,
        expiryDateYear: props.card_expiry_year,
        cvn: props.cvv,
        saveCard: !props.payment_required ? 1 : 0,
        storeForFuture: !props.payment_required ? 1 : 0,
      }),
      futurePaymentMethod: isBankAccount ? 'bankAccount' : 'creditCard',
      ...(isBankAccount && {
        accountName: props.bank_account_name,
        bsb: props.bank_account_bsb,
        accountNumber: props.bank_account_number,
      }),
      ...(isMonthlyCreditCard && {
        monthlyCardholderName: props.monthly_card_holder_name,
        monthlyCardNumber: props.monthly_card_number,
        monthlyExpiryDateMonth: props.monthly_card_expiry_month,
        monthlyExpiryDateYear: props.monthly_card_expiry_year,
      }),
    }
    setLoading(true)
    try {
      await VerificationApi.verifyOrder(payload)
      window.location.href = '/verification/success'
      return
    }
    catch (e: any) {
      if (e.response?.data?.errors) {
        const errors = e.response.data.errors

        if (orderDetails.checklist_required?.upfront_payment_required && !upfrontPaymentMade) {
          setUpfrontPaymentMade(true)
        }

        if (typeof errors === 'string') {
          setError('verifyChecked', { type: 'custom', message: errors })
        } else {
          const keyName = Object.keys(errors)[0];
          if (keyName === 'expiryDateYear') {
            setError('card_expiry_year', { type: 'custom', message: errors[keyName] })
          }
          else if (keyName === 'expiryDateMonth') {
            setError('card_expiry_month', { type: 'custom', message: errors[keyName] })
          }
          else if (keyName === 'cardNumber') {
            setError('card_number', { type: 'custom', message: errors[keyName] })
          }
          else if (keyName === 'bsb') {
            setError('bank_account_bsb', { type: 'custom', message: errors[keyName] })
          }
          else if (keyName === 'accountNumber') {
            setError('bank_account_number', { type: 'custom', message: errors[keyName] })
          }
          else {
            setError('verifyChecked', { type: 'custom', message: errors[keyName] })
          }
        }
      } else {
        setError('verifyChecked', { type: 'custom', message: 'An error occurred. Please try again.' })
      }
    }
    setLoading(false)
  }

  return <>
  <Box sx={{ textAlign: 'center', mt: 5, mb: 4, px: 4 }}>
    <Typography sx={{ fontWeight: 600 }}>To confirm your order for processing, please verify the details below,</Typography>
    <Typography sx={{ fontWeight: 600 }}>add payment information and accept the service Terms and Conditions.</Typography>
  </Box>
  <Grid container sx={{ p: 4, pt: 2, m: 'auto' }} maxWidth='900px'>
    <Grid item xs={12}>
      <Divider />
      <Typography sx={{ color: '#003187', fontWeight: 600, fontSize: '18px', mt: '15px', mb: '15px', pl: '30px' }}>Customer Information</Typography>
      <Divider />
    </Grid>
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Grid container>
        <Grid item lg={6} xs={12} sx={{ pl: '30px!important' }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px!important' }}>Business details</Typography>
          <Grid container sx={{ pl: '20px', mt: 1 }}>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Business name</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.business_name}</Typography></Grid>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Trading name</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.trading_name}</Typography></Grid>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>ABN/ACN</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.abn}</Typography></Grid>
          </Grid>
        </Grid>
        <Grid item lg={6} xs={12} sx={{ pl: '30px!important', '@media (max-width: 1199px)': { mt: 4 } }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px!important' }}>Contact details</Typography>
          <Grid container sx={{ pl: '20px' }}>
            <Grid item xs={12} sm={4} sx={infoGridStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Name</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.first_name} {orderDetails.last_name}</Typography></Grid>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Address</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.business_address1} {orderDetails.business_address2}, {orderDetails.business_suburb} {orderDetails.business_postcode}, {orderDetails.business_state}</Typography></Grid>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Phone</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.mobile_number}</Typography></Grid>
            <Grid item xs={12} sm={4} sx={infoGridHeaderStyle}><Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Email</Typography></Grid>
            <Grid item xs={12} sm={8} sx={infoGridStyle}><Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>{orderDetails.email}</Typography></Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 6 }} />
    </Grid>
    {false && <Grid xs={12} sx={{ pl: '10px', mt: '10px' }}>
      <FormGroup>
        <FormControlLabel control={<Checkbox />} label={<Typography sx={{ fontWeight: 550 }}>Tick this box to verify</Typography>} />
      </FormGroup>
    </Grid>}
    <Grid item xs={12}>
      <Grid container>
        <Grid item xs={12}>
          <Grid container sx={{ mt: '15px', mb: '15px', alignItems: 'center' }}>
            <Grid item xs={6}><Typography sx={{ color: '#003187', fontWeight: 600, fontSize: '18px', pl: '30px' }}>Order Summary</Typography></Grid>
            <Grid item xs={6} sx={{ textAlign: 'right', pr: '10px' }}><Typography sx={{ color: '#003187', fontWeight: 600, fontSize: '12px!important' }}>Your order number <Typography component="span" sx={{ fontWeight: 700, color: '#003187', fontSize: '12px!important' }}>#{orderDetails.order_id}</Typography> was created on  {orderDetails.order_date}</Typography></Grid>
          </Grid>
          <Divider />
        </Grid>
        {orderDetails.order_items.map((orderItem, key) => {
          if (orderItem.order_item_type === 'new') {
            return <VerificationNewItem key={key} orderItem={orderItem} />
          }
          else if (orderItem.order_item_type === 'change') {
            return <VerificationChangeItem key={key} orderItem={orderItem} />
          }
          else {
            return <></>
          }
        })}
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Divider />
        </Grid>
        <Grid item xs={12} sx={{ pl: '30px!important' }}>
          <Grid container alignItems='center' sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Typography sx={{ color: '#003187', fontWeight: 700, fontSize: '16px!important' }}>Total</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Subscription</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Once-off</Typography>
            </Grid>
          </Grid>
          <Grid container alignItems='center' sx={{ mt: 1 }}>
            <Grid item xs={6} sx={{ pl: '20px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '15px!important', color: '#242424' }}>Total monthly</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#003187' }}>${formatPrice((+orderDetails.total_charges.customer_subscription_fee))}</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }} />
          </Grid>
          <Grid container alignItems='center' sx={{ mt: 0.5 }}>
            <Grid item xs={6} sx={{ pl: '20px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '15px!important', color: '#242424' }}>Total once-off</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }} />
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#003187' }}>${formatPrice((+orderDetails.total_charges.onceoff_fee))}</Typography>
            </Grid>
          </Grid>
          {orderDetails.total_charges.discount_amount !== '0.00' && <Grid container alignItems='center' sx={{ mt: 0.5 }}>
            <Grid item xs={6} sx={{ pl: '20px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '15px!important', color: '#242424' }}>Total discount</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              {orderDetails.total_charges.subscription_discount !== '0.00' && <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#003187' }}>-${orderDetails.total_charges.subscription_discount}</Typography>}
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              {orderDetails.total_charges.onceoff_discount !== '0.00' && <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#003187' }}>-${orderDetails.total_charges.onceoff_discount}</Typography>}
            </Grid>
          </Grid>}
          <Typography sx={{ color: '#7A97B0', fontSize: '12px!important', mt: 1, pl: '20px' }}>All charges exclude GST</Typography>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Divider />
        </Grid>
        <Grid item xs={12} sx={{ pl: '30px!important' }}>
          <Grid container alignItems='center' sx={{ mt: 1.5 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 700, fontSize: '16px!important', color: '#003187' }}>Total Charges (ex.GST)</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '16px!important', color: '#003187' }}>${formatPrice((+orderDetails.total_charges.customer_subscription_fee - +orderDetails.total_charges.subscription_discount))}</Typography>
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '16px!important', color: '#003187' }}>${formatPrice((+orderDetails.total_charges.onceoff_fee - +orderDetails.total_charges.onceoff_discount))}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Divider />
        </Grid>
      </Grid>
    </Grid>


  </Grid>
    <Grid container sx={{ backgroundColor: '#E1F2FF', pt: 2, pb: 3, m: 'auto', borderRadius: '8px' }} maxWidth='900px'>
      <Grid item xs={12}>
        <Grid container sx={{ p: 4 }}>
          {orderDetails.checklist_required?.upfront_payment_required === 1 && !upfrontPaymentMade && orderDetails.upfront_payment &&
            <UpfrontPayment control={control} errors={errors} loading={loading} upfrontPayment={orderDetails.upfront_payment}
              paymentVerificationRequired={orderDetails.checklist_required?.payment_verification_required === 1 && orderDetails.provisioning_checklist?.payment_verified === 0}
              onDefaultChange={setUseDefaultMethod} />
          }
          {orderDetails.checklist_required?.payment_verification_required === 1 && orderDetails.provisioning_checklist?.payment_verified === 0 &&
            !(orderDetails.checklist_required?.upfront_payment_required === 1 && !upfrontPaymentMade && useDefaultMethod) &&
            <VerificationPayment setValue={setValue} control={control} errors={errors} loading={loading} />
          }
          <Grid item xs={12} sx={{ mt: 2 }}>
            <FormGroup>
              <Controller
                control={control}
                name='verifyChecked'
                render={({
                  field: { value, onChange },
                }) => {
                  return (
                    <FormControlLabel sx={{ alignItems: 'flex-start', mb: 1 }} control={<Checkbox disabled={loading || orderDetails.provisioning_checklist.customer_verified === 1} checked={value} onChange={onChange} sx={{ mt: -1 }} />} label={<Typography sx={{ fontSize: '14px!important' }}>By confirming this order, I agree to the Terms and Conditions as set by Alltel's
                      &nbsp;<Link style={LinkStyle
                      } target='_SFOA' to='https://www.alltel.com.au/docs/pdf/policies/Alltel-SFOA.pdf'>SFOA</Link>,
                      &nbsp;<Link style={LinkStyle
                      } target='_AUP' to='https://www.alltel.com.au/docs/pdf/policies/Alltel-Acceptable-Use.pdf'>Acceptable Use Policy</Link> and
                      &nbsp;<Link style={LinkStyle
                      } target='_PS' to='https://www.alltel.com.au/docs/pdf/policies/Alltel-Privacy-Statement.pdf'>Privacy Statement</Link>.</Typography>} />
                  );
                }}
              />
              <Controller
                control={control}
                name='paymentTermsChecked'
                render={({
                  field: { value, onChange },
                }) => {
                  return (
                    <FormControlLabel sx={{ alignItems: 'flex-start' }} control={<Checkbox disabled={loading || orderDetails.provisioning_checklist.customer_verified === 1} checked={value} onChange={onChange} sx={{ mt: -1 }} />} label={<Typography sx={{ fontSize: '14px!important' }}>I agree to Alltel's Payment Terms and understand that my service subscription will be billed pro-rata from the date my order is verified*, with ongoing monthly fees billed one month in advance as part of my regular billing cycle.<br/><Typography component="span" sx={{ fontStyle: 'italic', fontSize: '14px!important', color: '#888888' }}>*I understand that service activation may take up to 48 hours from order verification.</Typography></Typography>} />
                  );
                }}
              />
            </FormGroup>
            {(errors.verifyChecked || errors.paymentTermsChecked) && <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#FFF0F0', borderRadius: '6px', border: '1px solid #FFCDD2' }}>
              {errors.verifyChecked && <Typography sx={{ color: '#C62828', fontSize: '14px!important', fontWeight: 500 }}>{errors.verifyChecked.message}</Typography>}
              {errors.paymentTermsChecked && <Typography sx={{ color: '#C62828', fontSize: '14px!important', fontWeight: 500 }}>{errors.paymentTermsChecked.message}</Typography>}
            </Box>}
          </Grid>
          <Grid item xs={12} sm={5} md={3} sx={{ mt: 4 }}>
            {orderDetails.provisioning_checklist.customer_verified === 0 && <Button fullWidth disabled={loading} variant='contained' size='large' endIcon={loading === true ? <CircularProgress size="20px" /> : ''} onClick={handleSubmit(verifyOrder)} sx={{ background: 'linear-gradient(90deg, #FF4D6A 0%, #FF1744 100%)', color: 'white', fontWeight: 600, fontSize: '15px!important', textTransform: 'none', borderRadius: '8px', py: 1.5 }}>Confirm order</Button>}
            {orderDetails.provisioning_checklist.customer_verified === 1 && <><Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', fontStyle: 'italic' }}><CheckIcon color="success" /> Order Verified</Typography></>}

          </Grid>
        </Grid>
      </Grid>
    </Grid>

  </>
}

export default VerificationPage